import {
  useAbsenceTypes,
  useActivityTypeGroups,
  useTimeline,
} from '@src/features/absence';
import { useAuthentication } from '@src/features/auth';
import { useFormContext } from '@src/shared/form';
import { formatIsoDate } from '@src/shared/lib';
import { AutocompleteField } from '@src/shared/ui/fields';
import type { AutocompleteRenderGroupParams } from '@src/shared/ui/inputs';
import { List, ListItem } from '@src/shared/ui/List';
import Box from '@src/shared/ui/layout/Box';
import Typography from '@src/shared/ui/Typography';
import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { canBackdateAbsence } from '../../../lib';
import type { ScheduleFormValues } from '../Form';
import { StyleSheet } from './TypeField.styles';

type TypeFieldProps = {
  disabled?: boolean;
};

const UNGROUPED = '';

export default function TypeField({ disabled = false }: TypeFieldProps) {
  const { t } = useTranslation();
  const { watch, setValue } =
    useFormContext() as UseFormReturn<ScheduleFormValues>;
  const userId = watch('userId');
  const typeId = watch('typeId');
  const dateFrom = watch('dateFrom');
  const { users } = useTimeline();
  const { user } = useAuthentication();
  const targetUser = users.find((item) => item.id === userId);
  const inPast =
    !!dateFrom && formatIsoDate(dateFrom) < formatIsoDate(new Date());

  const { absenceTypes, isSuccess } = useAbsenceTypes({
    editableForUserId: userId,
  });
  const { activityTypeGroups } = useActivityTypeGroups();

  // Withdrawal keeps its original type. For bookings, a date/user change can
  // invalidate the current type; clear it so the required field is revalidated.
  useEffect(() => {
    if (
      !disabled &&
      inPast &&
      isSuccess &&
      typeId != null &&
      !canBackdateAbsence(typeId, absenceTypes, targetUser, user)
    ) {
      setValue('typeId', null, { shouldValidate: true });
    }
  }, [
    absenceTypes,
    disabled,
    inPast,
    isSuccess,
    setValue,
    targetUser,
    typeId,
    user,
  ]);

  const groupsMap = useMemo(
    () => new Map(activityTypeGroups.map((g) => [g.id, g])),
    [activityTypeGroups],
  );

  const options = useMemo(() => {
    const items = absenceTypes
      .filter(
        (item) =>
          disabled ||
          !inPast ||
          canBackdateAbsence(item.id, absenceTypes, targetUser, user),
      )
      .map((item) => {
        const group = item.groupId != null ? groupsMap.get(item.groupId) : null;
        return {
          label: item.name,
          value: item.id,
          group: group?.name ?? UNGROUPED,
        };
      });
    return items.sort((a, b) => b.group.localeCompare(a.group));
  }, [absenceTypes, disabled, groupsMap, inPast, targetUser, user]);

  const renderGroup = useMemo(() => {
    const groupStyleMap = new Map(
      activityTypeGroups.map((g) => [
        g.name,
        { fg: g.colourFg, bg: g.colourBg },
      ]),
    );

    return (params: AutocompleteRenderGroupParams) => {
      const colours = groupStyleMap.get(params.group);
      return (
        <ListItem key={params.key} disablePadding>
          <Box sx={StyleSheet.groupContainer}>
            {params.group !== UNGROUPED && (
              <Typography
                sx={StyleSheet.groupHeader(
                  colours?.fg ?? null,
                  colours?.bg ?? null,
                )}
              >
                {params.group}
              </Typography>
            )}
            <List disablePadding>{params.children}</List>
          </Box>
        </ListItem>
      );
    };
  }, [activityTypeGroups]);

  return (
    <AutocompleteField
      disabled={disabled}
      label={t('absence.schedule.field_type', 'Absence type')}
      name="typeId"
      options={options}
      groupBy={(option: (typeof options)[number]) => option.group}
      renderGroup={renderGroup}
      required
    />
  );
}
